import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["sourceApp"];
import { useCallback } from 'react';
import http from 'hub-http/clients/apiClient';
import { connect, useDispatch } from 'react-redux';
import { registerQuery, registerMutation, useQuery, useMutation } from 'data-fetching-client';
import getIn from 'immutable-less/transmute/getIn';
import Flow from 'automation-ui-canvas/flow/records/Flow/Flow';
import { addUserEditedActionId } from 'automation-ui-canvas/actions/FlowActions';
import * as AlertTypes from 'automation-ui-canvas/constants/AlertTypes';
import * as ActionTypes from 'automation-ui-canvas/actions/ActionTypes';
import * as messageActions from 'automation-ui-canvas/actions/MessageActions';
import * as FriendlyErrors from 'automation-ui-canvas/constants/FriendlyErrors';
import * as SourceApps from 'automation-ui-source-apps/constants/SourceApps';
import * as FlowValidationErrorTypes from 'automation-ui-flow-core/validation/FlowValidationErrorTypes';
import { updateBranchAction } from 'automation-ui-canvas/flow/utils/updateBranchAction';
import { updateAction } from 'automation-ui-canvas/flow/utils/updateAction';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { getCurrentlyStagedFlowByFlowId } from 'automation-ui-canvas/selectors/flowSelectors';
import { fetchWithEarlyRequestHook } from 'automation-ui-canvas/utils/apiDataUtils';
import { transformFlowValidationResponse } from 'automation-ui-flow-core/validation/validationUtils';
import { CONTACT_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import { jsx as _jsx } from "react/jsx-runtime";
/*******************************************************
 ************************ GET **************************
 *******************************************************/

export const SUMMARIES_GET_ENABLED_WORKFLOWS = registerQuery({
  fieldName: 'summariesGetEnabledWorkflows',
  args: ['isEnabled', 'excludePlatformSourceApps', 'limit', 'offset', 'name'],
  fetcher({
    isEnabled = true,
    excludePlatformSourceApps = [SourceApps.FORM_FOLLOWUP_EMAIL, SourceApps.LEAD_FLOW, SourceApps.FEEDBACK_FOLLOW_UP, SourceApps.FEEDBACK],
    objectTypeId = CONTACT_ID,
    limit,
    offset,
    name
  }) {
    return http.get(`automationapps/v1/summaries`, {
      query: {
        isEnabled,
        excludePlatformSourceApps,
        limit,
        offset,
        objectTypeId,
        name
      }
    }).catch(error => {
      const errorWithInfo = error;
      reportError({
        error: errorWithInfo,
        tags: {}
      });
      throw error;
    });
  }
});
export const FLOW_BY_ID_FIELDNAME = 'flowById';
export const GET_FLOW_BY_ID = registerQuery({
  fieldName: FLOW_BY_ID_FIELDNAME,
  args: ['flowId'],
  fetcher({
    flowId
  }) {
    if (!flowId) {
      return Promise.resolve(null);
    }
    return fetchWithEarlyRequestHook(() => http.get(`automationplatform/v1/hybrid/${flowId}`), 'flow-by-id').catch(error => {
      const errorWithInfo = error;
      reportError({
        error: errorWithInfo,
        tags: {}
      });
      throw error;
    }).then(flow => {
      return new Flow(flow);
    });
  }
});
export const FLOW_BY_UUID_FIELDNAME = 'flowByUuid';
export const GET_FLOW_BY_UUID = registerQuery({
  fieldName: FLOW_BY_UUID_FIELDNAME,
  args: ['flowUuid'],
  fetcher({
    flowUuid
  }) {
    return http.get(`automationplatform/v1/hybrid/uuid/${encodeURIComponent(flowUuid)}`).catch(error => {
      const errorWithInfo = error;
      reportError({
        error: errorWithInfo,
        tags: {},
        ignoreMeta: {
          errorStatus: [404]
        }
      });
      throw error;
    }).then(flow => new Flow(flow));
  }
});
export const FLOWS_BY_ID_FIELDNAME = 'flowsById';
export const GET_FLOWS_BY_ID = registerQuery({
  fieldName: FLOWS_BY_ID_FIELDNAME,
  args: ['flowIds'],
  fetcher({
    flowIds = []
  }) {
    return http.post('automationplatform/v1/hybrid/ids/fetch', {
      data: flowIds
    }).catch(error => {
      const errorWithInfo = error;
      reportError({
        error: errorWithInfo,
        tags: {}
      });
    }).then(flows => {
      const flowMap = {};
      Object.values(flows).forEach(flow => {
        flowMap[flow.flowId] = new Flow(flow);
      });
    });
  }
});
export const getFlowByIdData = res => getIn(['data', FLOW_BY_ID_FIELDNAME], res);
export const getFlowByUuidData = res => getIn(['data', FLOW_BY_UUID_FIELDNAME], res);
export const useGetFlowById = flowId => {
  const query = useQuery(GET_FLOW_BY_ID, {
    variables: {
      flowId: flowId
    },
    skip: !flowId
  });
  return query;
};
export const useGetFlowByUuid = flowUuid => {
  const query = useQuery(GET_FLOW_BY_UUID, {
    variables: {
      flowUuid
    },
    skip: !flowUuid
  });
  return query;
};

/**
 * HOC used to retrieve the active flow by id. In the case that
 * there is a staged flow in the redux store, it will take
 * precedense over the fetched flow in the cache.
 *
 * Returns the `flow` of type Flow as well as the `flowQuery`
 * of type ApolloQuery. Use the latter to access loading and
 * error states, as well as all other Apollo methods.
 */

export const wireFlowById = WrappedComponent => {
  const UnconnectedWiredFlowById = props => {
    var _flowQuery$data;
    const flowQuery = useGetFlowById(props.flowId);
    let flow = (_flowQuery$data = flowQuery.data) === null || _flowQuery$data === void 0 ? void 0 : _flowQuery$data.flowById;
    if (props.currentlyStagedFlow && flow && (props.currentlyStagedFlow.version === flow.getVersion() || props.currentlyStagedFlow.stagedVersion)) {
      flow = props.currentlyStagedFlow;
    }
    return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
      flowQuery: flowQuery,
      flow: flow
    }));
  };

  // @ts-expect-error TODO: Come back to this
  const ConnectedWiredFlowById = connect((state, props) => {
    const currentlyStagedFlow = props.flowId ? getCurrentlyStagedFlowByFlowId(props.flowId)(state) : null;
    return {
      currentlyStagedFlow
    };
  }, null)(UnconnectedWiredFlowById);
  return ConnectedWiredFlowById;
};
export const wireFlowByUuid = WrappedComponent => props => {
  const flowQuery = useGetFlowByUuid(props.flowUuid);
  const flow = flowQuery.data;
  if (!props.flowUuid) {
    return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props));
  }
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    flowQuery: flowQuery,
    flow: flow
  }));
};

/*******************************************************
 ********************** UPDATE *************************
 *******************************************************/

export const defaultErrorHandlingForUpdateFlow = ({
  error,
  dispatch
}) => {
  const response = getIn(['networkError', 'responseJSON'], error);
  const errorType = getIn(['errorType'], response);
  const responseMessage = getIn(['message'], response);
  let responseMessageJSON;
  try {
    responseMessageJSON = JSON.parse(responseMessage || '');
  } catch (err) {
    // Message was not JSON serializable
  }
  if (errorType) {
    switch (errorType) {
      case FriendlyErrors.OUTDATED_FLOW_VERSION:
        dispatch(messageActions.addAlert(AlertTypes.OUTDATED_FLOW_VERSION));
        break;
      default:
        dispatch(messageActions.addAlert(AlertTypes.UPDATE_ACTION_FAILED));
        break;
    }
    return;
  } else if ( /* Check for validation error indicating that a reference to an action */
  responseMessage && responseMessageJSON) {
    const validationInfo = transformFlowValidationResponse(responseMessageJSON);
    const flowErrors = validationInfo.getFlowErrors();
    flowErrors.forEach(flowError => {
      if (flowError.flowValidationErrorType === FlowValidationErrorTypes.REFERENCED_ACTION_NOT_IN_PATH_TO_ACTION) {
        dispatch(messageActions.addAlert(AlertTypes.UPDATE_REFERENCED_ACTION_FAILED));
      } else if (flowError.flowValidationErrorType === FlowValidationErrorTypes.EXCEEDED_ACTIONS_LIMIT) {
        dispatch(messageActions.addAlert(AlertTypes.ACTION_LIMIT_REACHED, {
          body: {
            limit: flowError.contextMap.actionLimit
          }
        }));
      }
    });
    return;
  }
  dispatch(messageActions.addAlert(AlertTypes.UPDATE_ACTION_FAILED));
  reportError({
    error
  });
};
export const UPDATE_FLOW = registerMutation({
  fieldName: FLOW_BY_ID_FIELDNAME,
  args: ['flow', 'sourceApp', 'enrollmentSettingsWriteSource', 'aiAssistants'],
  fetcher({
    flow,
    sourceApp,
    enrollmentSettingsWriteSource,
    aiAssistants
  }) {
    const data = enrollmentSettingsWriteSource ? Object.assign({}, flow.toJS(), {
      enrollmentSettingsWriteSource
    }) : flow;
    return http.post(`automationplatform/v1/hybrid/batch`, {
      data,
      query: {
        aiAssistants,
        sourceapp: sourceApp
      }
    });
  }
});
export const updateFlowMutationCache = (flow, cache, dispatch, res) => {
  const data = Object.assign({}, res ? res.data : {}, {
    [FLOW_BY_ID_FIELDNAME]: flow
  });
  cache.writeQuery({
    query: GET_FLOW_BY_ID,
    variables: {
      flowId: flow.flowId
    },
    data
  });
  dispatch({
    type: ActionTypes.UPDATE_FLOW_LOCALLY,
    payload: {
      flow: null
    }
  });
};
export const handleUpdateFlowMutation = dispatch => (cache, res) => {
  const newFlow = getFlowByIdData(res);
  if (!newFlow) {
    return;
  }
  const newFlowRecord = new Flow(newFlow);
  updateFlowMutationCache(newFlowRecord, cache, dispatch, res);
};
export const useUpdateFlow = () => {
  const dispatch = useDispatch();
  return useMutation(UPDATE_FLOW, {
    update: handleUpdateFlowMutation(dispatch)
  });
};
export function useUpdateActionAndSaveFlow() {
  const dispatch = useDispatch();
  const [updateFlowMutation] = useUpdateFlow();
  return useCallback(({
    flow,
    action,
    edgeId,
    sourceApp
  }) => {
    const updatedFlow = updateAction({
      action,
      flow,
      edgeId
    });
    return updateFlowMutation({
      variables: {
        flow: updatedFlow,
        sourceApp
      }
    }).then(res => {
      const flowResponse = getFlowByIdData(res);
      dispatch(addUserEditedActionId({
        flow: new Flow(flowResponse),
        action
      }));
      return flowResponse;
    });
  }, [updateFlowMutation, dispatch]);
}
/* could use this in wireUpdateFlow but don't want to risk change in
top-level components for current PR */
export const useGetUpdateFlow = () => {
  const [updateFlowMutation] = useUpdateFlow();
  const updateFlow = variables => updateFlowMutation({
    variables
  }).then(getFlowByIdData);
  return updateFlow;
};
export const wireUpdateFlow = WrappedComponent => props => {
  const [updateFlowMutation] = useUpdateFlow();
  const updateFlow = variables => updateFlowMutation({
    variables
  }).then(getFlowByIdData);
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    updateFlow: updateFlow
  }));
};
export const wireUpdateBranchActionAndSaveFlow = WrappedComponent => props => {
  const dispatch = useDispatch();
  const [updateFlowMutation] = useUpdateFlow();
  const _updateBranchActionAndSaveFlow = _ref => {
    let {
        sourceApp
      } = _ref,
      options = _objectWithoutPropertiesLoose(_ref, _excluded);
    const updatedFlow = updateBranchAction(Object.assign({}, options));
    return updateFlowMutation({
      variables: {
        flow: updatedFlow,
        sourceApp
      }
    }).then(res => {
      const flowResponse = getFlowByIdData(res);
      dispatch(addUserEditedActionId({
        flow: new Flow(flowResponse),
        action: options.action
      }));
      return flowResponse;
    });
  };
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    updateBranchActionAndSaveFlow: _updateBranchActionAndSaveFlow
  }));
};
export const wireUpdateActionAndSaveFlow = WrappedComponent => props => {
  const _updateActionAndSaveFlow = useUpdateActionAndSaveFlow();
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    updateActionAndSaveFlow: _updateActionAndSaveFlow
  }));
};
const makeEnrollmentSchedulePatch = enrollmentSchedule => {
  if (enrollmentSchedule === undefined) {
    return undefined;
  }
  if (enrollmentSchedule === null) {
    return {
      operation: 'DELETE'
    };
  }
  return {
    operation: 'UPDATE',
    value: enrollmentSchedule
  };
};
export const wireUpdateEventEnrollmentCriteria = WrappedComponent => props => {
  const {
    flow,
    updateFlowLists,
    addAlert
  } = props;
  const updateEnrollmentCriteria = useCallback(({
    associatedLists,
    flowEventFilters,
    shouldReenroll,
    enrollmentTrigger,
    enrollmentSchedule,
    enrollmentCriteria,
    classicEnrollmentSettings
  }, onSuccess = () => {}, onError = __err => {}) => {
    return updateFlowLists({
      flowId: flow.flowId,
      opts: {
        data: {
          associatedLists,
          shouldReenroll,
          flowEventFilters,
          enrollmentTrigger,
          enrollmentSchedule: makeEnrollmentSchedulePatch(enrollmentSchedule),
          enrollmentCriteria,
          classicEnrollmentSettings
        }
      },
      sourceApp: SourceApps.WORKFLOWS_APP
    }).then(onSuccess).catch(err => {
      onError(err);
      addAlert(AlertTypes.SAVED_WORKFLOW_ENROLLMENT_FAILED, {
        body: {}
      });
    });
  }, [flow, updateFlowLists, addAlert]);
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    updateEnrollmentCriteria: updateEnrollmentCriteria
  }));
};

/*******************************************************
 ********************** DELETE *************************
 *******************************************************/

export function useDeleteAction() {
  const [updateFlowMutation] = useUpdateFlow();
  const dispatch = useDispatch();
  return useCallback(({
    flow,
    sourceApp,
    actionToDeleteId,
    branchIndexToKeep,
    removeAllChildren = false
  }) => {
    const newFlow = flow.removeAction(Number(actionToDeleteId), removeAllChildren, branchIndexToKeep).getSanitizedFlow();
    return updateFlowMutation({
      variables: {
        flow: newFlow,
        sourceApp
      }
    }).catch(() => {
      dispatch(messageActions.addAlert(AlertTypes.DELETED_ACTION_FAILED));
    }).then(getFlowByIdData);
  }, [updateFlowMutation, dispatch]);
}
export const wireDeleteAction = WrappedComponent => props => {
  const deleteAction = useDeleteAction();
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    deleteAction: deleteAction
  }));
};